import type { Option } from '@app/src/pages/ResourceCollection/Filters/FilterAutocomplete'
import FilterAutocomplete, {
  getAutocompleteHeightControlStyles,
} from '@app/src/pages/ResourceCollection/Filters/FilterAutocomplete'
import type { AutocompleteGetTagProps, AutocompleteProps, TextFieldProps } from '@mui/material'
import React, { useEffect } from 'react'

export { getAutocompleteHeightControlStyles }
export type { Option }

type OnChangeMultiple = (value: string[], option: Option[] | null, replace?: boolean) => void
type OnChangeSingle = (value: string | null, option: Option | null, replace?: boolean) => void

export interface FilterSelectCommonProps {
  options: Option[]
  fieldLabel: string
  required?: boolean
  groupBy?: AutocompleteProps<Option, false, undefined, undefined>['groupBy']
  size?: AutocompleteProps<never, never, never, never>['size']
  actionButtons?: JSX.Element
  modifiers?: JSX.Element
  renderOption?: AutocompleteProps<Option, boolean, false, false, 'div'>['renderOption']
  renderTags?: (value: Option[], getTagProps: AutocompleteGetTagProps) => JSX.Element[]
  variant?: TextFieldProps['variant']
  preSelectedValue?: string | number
  helperText?: string
}

interface SingleModeProps {
  multiple: false
  value: string
  onChange: OnChangeSingle
}

interface MultipleModeProps {
  multiple: true
  value: string[]
  onChange: OnChangeMultiple
}

export type FilterSelectTruncateProps = SingleModeProps | MultipleModeProps

export type FilterSelectProps = FilterSelectCommonProps & FilterSelectTruncateProps

const isMultipleValue = (value: Option | Option[] | null): value is Option[] => Array.isArray(value)

const isMultipleStringValue = (value: string | string[] | null): value is string[] => Array.isArray(value)

const isOption = (value: unknown): value is Option =>
  typeof (value as Option)?.label !== 'undefined' && typeof (value as Option)?.value !== 'undefined'

const findValue = (options: Option[], flatValue: string): Option | undefined =>
  options?.find((option): boolean => option.value.toString() === flatValue)

const getSingleSelectValue = (options: Option[], value: string): Option | null => findValue(options, value) || null

const getMultipleSelectValue = (options: Option[], value: string[] = []): Option[] =>
  value.map((flatValueItem: string) => findValue(options, flatValueItem)).filter<Option>(isOption)

export const optionToValue = (option?: Option | null): string => {
  if (!option) return ''
  return (option?.value ?? '').toString()
}

export const optionToValueArray = (option?: Option | Option[] | null): string[] => {
  if (!option) return []
  return [option].flat().map(option => (option?.value ?? '').toString())
}

function FilterSelect({
  value,
  options,
  fieldLabel,
  required,
  groupBy,
  onChange,
  multiple,
  size,
  modifiers,
  actionButtons,
  renderOption,
  renderTags,
  variant = 'outlined',
  preSelectedValue,
  helperText,
}: FilterSelectProps): JSX.Element {
  useEffect(() => {
    if (!value && preSelectedValue && options.length > 0) {
      const preselectedOption = options.find(
        option => option.value.toString() === preSelectedValue.toString() || option.label === preSelectedValue,
      )

      if (preselectedOption) {
        if (multiple) {
          const onChangeMultiple = onChange as OnChangeMultiple
          onChangeMultiple([preselectedOption.value.toString()], [preselectedOption], true)
        } else {
          const onChangeSingle = onChange as OnChangeSingle
          onChangeSingle(preselectedOption.value.toString(), preselectedOption, true)
        }
      }
    }
  }, [options, preSelectedValue, value, multiple, onChange])

  const truncateProps: { multiple: true; value: Option[] } | { multiple: false; value: Option | null } =
    isMultipleStringValue(value) && multiple
      ? { multiple: true, value: getMultipleSelectValue(options, value) }
      : { multiple: false, value: getSingleSelectValue(options, value as string) }

  const handleChange = (option: Option | Option[] | null) => {
    if ((multiple || isMultipleValue(option)) && option !== null) {
      const onChangeMultiple: OnChangeMultiple = onChange as OnChangeMultiple
      onChangeMultiple(optionToValueArray(option), [option].flat())
    } else {
      const onChangeSingle: OnChangeSingle = onChange as OnChangeSingle
      onChangeSingle(optionToValue(option), option as Option | null)
    }
  }

  return (
    <FilterAutocomplete
      {...truncateProps}
      onChange={(_event, option) => handleChange(option)}
      options={options}
      fieldLabel={fieldLabel}
      required={required}
      groupBy={groupBy}
      size={size}
      variant={variant}
      helperText={helperText}
      actionButtons={actionButtons}
      modifiers={modifiers}
      renderOption={renderOption}
      renderTags={renderTags}
    />
  )
}

export default FilterSelect
